import { apiUrl } from '../../../gemeinsam/lib/plattform';
import { Erfolgsmeldung } from '../../../gemeinsam/komponenten/Meldungen';
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { api, ApiFehler } from '../../../gemeinsam/lib/api';
import { useDaten, euro, datum, menge, preis } from '../../../gemeinsam/lib/daten';
import { belegOeffnen } from '../lib/drucken';
import type { Artikel, Kunde, Rechnung } from '../../../gemeinsam/lib/typen';
import {
  Abzeichen, Auswahl, Feld, Hinweis, Karte, KartenKopf, Knopf, LeerZustand, Spinner,
} from '../../../gemeinsam/komponenten/Basis';
import { useSprache, type Schluessel } from '../../../gemeinsam/lib/i18n';
import { Fenster } from '../../../gemeinsam/komponenten/Fenster';
import { BezahltStempel, GiroCode } from '../komponenten/Zahlstatus';
import {
  leerePosition, PositionenEditor, positionenFuerApi, type Entwurfsposition,
} from '../komponenten/Positionen';

type Antwort = {
  rechnungen: Rechnung[];
  summen: { anzahl: number; offenAnzahl: number; offenBetrag: number; umsatzBezahlt: number };
};

/** Was nach Zahlungen aus dem Bankabgleich noch offen ist. */
const offenerBetrag = (r: Rechnung) =>
  Math.round((r.brutto - (r.zahlungen ?? []).reduce((s, z) => s + z.betrag, 0)) * 100) / 100;

const ZAHLWEG_TEXT: Record<string, Schluessel> = {
  ueberweisung: 'rechnungen.zahlweg.ueberweisung', bar: 'rechnungen.zahlweg.bar', karte: 'rechnungen.zahlweg.karte',
  guthaben: 'rechnungen.guthaben',
};

const heute = () => new Date().toISOString().slice(0, 10);
/** „1.234,50" und „59.50" — mit Komma ist der Punkt Tausendertrenner, sonst Dezimalpunkt. */
const zahlEingabe = (s: string) => {
  const t = String(s).trim();
  return Number(t.includes(',') ? t.replace(/\./g, '').replace(',', '.') : t) || 0;
};

type Entwurf = {
  kundeId: string; positionen: Entwurfsposition[]; rabatt: string;
  zahlweg: 'ueberweisung' | 'bar' | 'karte';
  /** 'kunde' = Ziel aus der Kundenakte, Zahl = Tage, 'datum' = eigenes Datum */
  ziel: string; faelligAm: string; bezahlt: boolean; lieferschein: boolean;
};

const LEERER_ENTWURF = (): Entwurf => ({
  kundeId: '', positionen: [leerePosition()], rabatt: '0',
  zahlweg: 'ueberweisung', ziel: 'kunde', faelligAm: '', bezahlt: false, lieferschein: true,
});

const STATUS_FARBE = {
  entwurf: 'neutral', gestellt: 'gelb', bezahlt: 'gruen', storniert: 'rot',
} as const;

export default function Rechnungen() {
  const { t } = useSprache();
  // Die Übersicht verlinkt „offene Rechnungen" als /rechnungen?status=gestellt.
  const ort = useLocation();
  const [status, setStatus] = useState(() => new URLSearchParams(ort.search).get('status') || '');
  const { daten, laedt, fehler, neuLaden } = useDaten<Antwort>(
    `/invoices${status ? `?status=${status}` : ''}`, [status]);
  const { daten: kunden } = useDaten<Kunde[]>('/customers');
  const { daten: artikelDaten } = useDaten<{ produkte: Artikel[] }>('/products');

  const [neu, setNeu] = useState<Entwurf | null>(null);
  const [zahlungForm, setZahlungForm] = useState<{ betrag: string; datum: string; zahlart: string } | null>(null);
  const [gutschriftForm, setGutschriftForm] = useState<{ mengen: string[]; grund: string } | null>(null);
  const [offen, setOffen] = useState<Rechnung | null>(null);
  const [sendeFehler, setSendeFehler] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [sendet, setSendet] = useState(false);

  // Aus der Kundenakte: ?id= öffnet die Rechnung, ?neu=1&kunde= schreibt eine
  // für diesen Kunden.
  useEffect(() => {
    const p = new URLSearchParams(ort.search);
    const id = p.get('id');
    if (id) api<Rechnung>(`/invoices/${id}`).then(setOffen).catch(() => setSendeFehler('Rechnung nicht gefunden.'));
    if (p.get('neu') === '1') setNeu({ ...LEERER_ENTWURF(), kundeId: p.get('kunde') || '' });
  }, [ort.search]);

  const artikel = artikelDaten?.produkte ?? [];

  async function drucken(r: Rechnung) {
    const problem = await belegOeffnen(apiUrl(`/api/invoices/${r.id}/druck`));
    if (problem) setSendeFehler(problem);
  }

  async function anlegen(alsStatus: 'entwurf' | 'gestellt') {
    if (!neu) return;
    setSendeFehler(null);
    setSendet(true);
    try {
      const rechnung = await api<Rechnung>('/invoices', {
        methode: 'POST',
        daten: {
          kundeId: neu.kundeId || undefined,
          kundeName: neu.kundeId ? undefined : 'Barverkauf',
          // „Bereits bezahlt" gilt nur beim Stellen — ein Entwurf ist nie bezahlt.
          status: alsStatus === 'gestellt' && neu.bezahlt ? 'bezahlt' : alsStatus,
          zahlweg: neu.zahlweg,
          zahlungszielTage: /^\d+$/.test(neu.ziel) ? Number(neu.ziel) : undefined,
          faelligAm: neu.ziel === 'datum' && neu.faelligAm ? neu.faelligAm : undefined,
          lieferschein: neu.lieferschein,
          rabattProzent: Number(neu.rabatt.replace(',', '.')) || 0,
          positionen: positionenFuerApi(neu.positionen),
        },
      });
      setNeu(null);
      setMeldung(alsStatus === 'entwurf' ? 'Entwurf gespeichert.'
        : rechnung.lieferschein ? t('rechnungen.gestelltMitLs').replace('{nummer}', rechnung.lieferschein.nummer)
        : 'Rechnung gestellt.');
      // Gleich öffnen: dort liegen Rechnung und Lieferschein zum Drucken bereit.
      if (alsStatus !== 'entwurf') { setOffen(rechnung); setZahlungForm(null); }
      await neuLaden();
    } catch (e) {
      setSendeFehler(e instanceof ApiFehler ? e.message : 'Rechnung konnte nicht angelegt werden.');
    } finally {
      setSendet(false);
    }
  }

  // Storno und Gutschrift sind zwei verschiedene Dinge: das Storno hebt die
  // ganze Rechnung auf, die Gutschrift erstattet einen Teil und bleibt als
  // eigener Beleg bestehen. Beides muss erreichbar sein.
  // Gutschrift direkt an der Rechnung: Mengen wählen, Grund, stellen — sie
  // wirkt sofort (verrechnet mit der offenen Rechnung oder als Guthaben).
  // Früher entstand sie nur als Entwurf unter Belege und wirkte erst nach
  // dem Stellen dort; das sah aus, als wäre nichts passiert.
  async function gutschriftStellen(r: Rechnung) {
    if (!gutschriftForm) return;
    const positionen = r.positionen
      .map((_, index) => ({ index, menge: zahlEingabe(gutschriftForm.mengen[index] ?? '0') }))
      .filter(p => p.menge > 0);
    if (!positionen.length) { setSendeFehler('Bitte bei mindestens einer Position eine Menge angeben.'); return; }
    setSendeFehler(null); setSendet(true);
    try {
      const gs = await api<{ id: string }>('/belege/gutschrift-zu-rechnung', {
        methode: 'POST', daten: { rechnungId: r.id, positionen, grund: gutschriftForm.grund },
      });
      const gestellt = await api<{ nummer: string }>(`/belege/${gs.id}/status`, { methode: 'POST', daten: { status: 'gestellt' } });
      setOffen(await api<Rechnung>(`/invoices/${r.id}`));
      setGutschriftForm(null);
      setMeldung(t('rechnungen.gutschriftErstellt').replace('{nummer}', gestellt.nummer));
      await neuLaden();
    } catch (e) {
      setSendeFehler(e instanceof ApiFehler ? e.message : 'Gutschrift konnte nicht gestellt werden.');
    } finally { setSendet(false); }
  }

  async function gutschriftDrucken(r: Rechnung) {
    if (!r.gutschrift) return;
    const problem = await belegOeffnen(apiUrl(`/api/belege/${r.gutschrift.id}/druck`));
    if (problem) setSendeFehler(problem);
  }

  async function statusSetzen(r: Rechnung, neuerStatus: string) {
    if (neuerStatus === 'storniert' && !confirm(
      `Rechnung ${r.nummer} stornieren?\n\nDie Ware wird ins Lager zurückgebucht. Ein Storno ist endgültig.`)) return;
    try {
      await api(`/invoices/${r.id}/status`, { methode: 'POST', daten: { status: neuerStatus } });
      setMeldung(`Rechnung ${r.nummer || ''} ist jetzt ${neuerStatus}.`);
      setOffen(null);
      await neuLaden();
    } catch (e) {
      setSendeFehler(e instanceof ApiFehler ? e.message : 'Statuswechsel fehlgeschlagen.');
    }
  }

  async function lieferscheinDrucken(r: Rechnung) {
    setSendeFehler(null);
    try {
      const ls = r.lieferschein
        ?? await api<{ id: string; nummer: string; status: string }>(`/invoices/${r.id}/lieferschein`, { methode: 'POST' });
      if (!r.lieferschein) setOffen({ ...r, lieferschein: ls });
      const problem = await belegOeffnen(apiUrl(`/api/belege/${ls.id}/druck`));
      if (problem) setSendeFehler(problem);
      if (!r.lieferschein) await neuLaden();
    } catch (e) {
      setSendeFehler(e instanceof ApiFehler ? e.message : 'Lieferschein konnte nicht erstellt werden.');
    }
  }

  async function zahlungSpeichern(r: Rechnung) {
    if (!zahlungForm) return;
    setSendeFehler(null); setSendet(true);
    try {
      const neu = await api<Rechnung>(`/invoices/${r.id}/zahlungen`, {
        methode: 'POST',
        daten: { betrag: zahlEingabe(zahlungForm.betrag), datum: zahlungForm.datum, zahlart: zahlungForm.zahlart },
      });
      setOffen(neu);
      setZahlungForm(null);
      setMeldung(neu.status === 'bezahlt' ? t('rechnungen.vollBezahlt') : t('rechnungen.zahlungGespeichert'));
      await neuLaden();
    } catch (e) {
      setSendeFehler(e instanceof ApiFehler ? e.message : 'Zahlung konnte nicht gespeichert werden.');
    } finally { setSendet(false); }
  }

  async function zahlungEntfernen(r: Rechnung, zahlungId: string) {
    if (!confirm(`${t('rechnungen.zahlungEntfernen')}?`)) return;
    try {
      setOffen(await api<Rechnung>(`/invoices/${r.id}/zahlungen/${zahlungId}`, { methode: 'DELETE' }));
      await neuLaden();
    } catch (e) {
      setSendeFehler(e instanceof ApiFehler ? e.message : 'Zahlung konnte nicht entfernt werden.');
    }
  }

  function schliessen() {
    setOffen(null); setZahlungForm(null); setGutschriftForm(null); setSendeFehler(null);
  }

  const liste = daten?.rechnungen ?? [];
  const gewaehlterKunde = (kunden ?? []).find(k => k.id === neu?.kundeId);

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">{t('rechnungen.titel')}</h1>
          {daten && (
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
              {daten.summen.anzahl} Belege · {euro(daten.summen.offenBetrag)} offen
              {daten.summen.offenAnzahl > 0 && ` (${daten.summen.offenAnzahl})`}
            </p>
          )}
        </div>
        {!neu && (
          <Knopf variante="primaer" onClick={() => { setNeu(LEERER_ENTWURF()); setMeldung(null); }}>
            Rechnung schreiben
          </Knopf>
        )}
      </div>

      {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
      {sendeFehler && !offen && <Hinweis art="fehler">{sendeFehler}</Hinweis>}
      <Erfolgsmeldung text={meldung} />

      {neu && (
        <Karte>
          <KartenKopf titel={t('rechnungen.neu')} hinweis={t('rechnungen.entwurfHinweis')} />
          <div className="space-y-4 p-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <Auswahl label={t('feld.kunde')} value={neu.kundeId} onChange={e => setNeu(n => n && ({ ...n, kundeId: e.target.value }))}>
                <option value="">{t('rechnungen.barverkauf')}</option>
                {(kunden ?? []).map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
              </Auswahl>
              <Feld label={t('belege.rabatt')} value={neu.rabatt} inputMode="decimal"
                    onChange={e => setNeu(n => n && ({ ...n, rabatt: e.target.value }))} />
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{t('feld.positionen')}</p>
              <PositionenEditor positionen={neu.positionen} artikel={artikel}
                                setzen={p => setNeu(n => n && ({ ...n, positionen: p }))} />
            </div>

            <div className="space-y-3 rounded-lg border border-slate-200 p-4 dark:border-slate-800">
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{t('rechnungen.zahlung')}</p>
              <div className="grid gap-3 sm:grid-cols-3">
                <Auswahl label={t('rechnungen.zahlart')} value={neu.zahlweg}
                         onChange={e => setNeu(n => n && ({ ...n, zahlweg: e.target.value as Entwurf['zahlweg'] }))}>
                  {(['ueberweisung', 'bar', 'karte'] as const).map(z => <option key={z} value={z}>{t(ZAHLWEG_TEXT[z])}</option>)}
                </Auswahl>
                <Auswahl label={t('rechnungen.bezahlung')} value={neu.bezahlt ? 'ja' : 'nein'}
                         onChange={e => setNeu(n => n && ({ ...n, bezahlt: e.target.value === 'ja' }))}>
                  <option value="nein">{t('rechnungen.offenLassen')}</option>
                  <option value="ja">{t('rechnungen.schonBezahlt')}</option>
                </Auswahl>
                {!neu.bezahlt && (
                  <Auswahl label={t('rechnungen.zahlungsziel')} value={neu.ziel}
                           onChange={e => setNeu(n => n && ({ ...n, ziel: e.target.value }))}>
                    <option value="kunde">
                      {t('rechnungen.zielKunde').replace('{tage}', String(gewaehlterKunde?.zahlungszielTage ?? 14))}
                    </option>
                    <option value="0">{t('rechnungen.zielSofort')}</option>
                    {[7, 14, 30, 60].map(n => <option key={n} value={String(n)}>{t('rechnungen.zielTage').replace('{tage}', String(n))}</option>)}
                    <option value="datum">{t('rechnungen.zielDatum')}</option>
                  </Auswahl>
                )}
              </div>
              {!neu.bezahlt && neu.ziel === 'datum' && (
                <div className="sm:w-1/3">
                  <Feld label={t('rechnungen.faelligAm')} type="date" value={neu.faelligAm} min={heute()}
                        onChange={e => setNeu(n => n && ({ ...n, faelligAm: e.target.value }))} />
                </div>
              )}
              <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                <input type="checkbox" checked={neu.lieferschein}
                       onChange={e => setNeu(n => n && ({ ...n, lieferschein: e.target.checked }))} />
                {t('rechnungen.lieferscheinMit')}
              </label>
            </div>

            <div className="flex flex-wrap gap-2 border-t border-slate-200 pt-4 dark:border-slate-800">
              <Knopf variante="primaer" laedt={sendet} onClick={() => anlegen('gestellt')}>{t('rechnungen.stellen')}</Knopf>
              <Knopf laedt={sendet} onClick={() => anlegen('entwurf')}>{t('rechnungen.entwurfSpeichern')}</Knopf>
              <Knopf onClick={() => { setNeu(null); setSendeFehler(null); }}>{t('aktion.abbrechen')}</Knopf>
            </div>
          </div>
        </Karte>
      )}

      {offen && (
        <Fenster
          beiSchliessen={schliessen}
          titel={<span className="flex flex-wrap items-center gap-2">
            {`Rechnung ${offen.nummer || '(Entwurf)'}`}
            <Abzeichen art={STATUS_FARBE[offen.status]}>{offen.status}</Abzeichen>
          </span>}
          unterzeile={`${offen.kundeName} · ${datum(offen.datum)} · ${t('rechnungen.zahlart')}: ${t(ZAHLWEG_TEXT[offen.zahlweg ?? 'ueberweisung'])}`
            + (offen.status === 'gestellt'
              ? ` · ${offen.zahlungszielTage === 0 ? t('rechnungen.sofort') : `${t('rechnungen.faelligAm')} ${datum(offen.faelligAm)}`}` : '')
            + (offen.lieferschein ? ` · ${t('rechnungen.lieferschein')} ${offen.lieferschein.nummer}` : '')}
          aktionen={offen.kundeId && (
            <Link to={`/kunden/${offen.kundeId}`} className="rounded-lg px-3 py-2 text-sm font-medium text-marke-700 hover:bg-marke-50 dark:text-marke-300 dark:hover:bg-marke-950/40">
              {t('kunden.akte')}
            </Link>
          )}
        >
          <div className="space-y-4 p-5">
            {/* Fehler hier wiederholen — oben auf der Seite liegen sie hinter dem Fenster. */}
            {sendeFehler && <Hinweis art="fehler">{sendeFehler}</Hinweis>}
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-slate-500 dark:text-slate-400">
                <tr><th className="pb-2">{t('feld.position')}</th><th className="pb-2 text-right">{t('feld.menge')}</th>
                    <th className="pb-2 text-right">{t('belege.einzel')}</th><th className="pb-2 text-right">{t('feld.netto')}</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                {offen.positionen.map((p, i) => (
                  <tr key={i}>
                    <td className="py-2 text-slate-900 dark:text-slate-100">{p.bezeichnung}</td>
                    <td className="py-2 text-right text-slate-600 dark:text-slate-400">{menge(p.menge, p.einheit)}</td>
                    <td className="py-2 text-right text-slate-600 dark:text-slate-400">{preis(p.einzelpreis)}</td>
                    <td className="py-2 text-right text-slate-900 dark:text-slate-100">{euro(p.netto)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex flex-col-reverse gap-4 border-t border-slate-200 pt-3 sm:flex-row sm:items-start dark:border-slate-800">
            <div className="flex-1 space-y-1 text-sm">
              <div className="flex justify-between text-slate-600 dark:text-slate-400"><span>{t('feld.netto')}</span><span>{euro(offen.netto)}</span></div>
              {offen.steuern.map(s => (
                <div key={s.satz} className="flex justify-between text-slate-600 dark:text-slate-400">
                  <span>Umsatzsteuer {s.satz} %</span><span>{euro(s.steuer)}</span>
                </div>
              ))}
              <div className="flex justify-between text-base font-bold text-slate-900 dark:text-slate-100">
                <span>{t('feld.gesamt')}</span><span>{euro(offen.brutto)}</span>
              </div>
              {(offen.zahlungen?.length ?? 0) > 0 && (
                <div className="mt-2 space-y-1 border-t border-slate-200 pt-2 dark:border-slate-800">
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">{t('rechnungen.zahlungen')}</p>
                  {offen.zahlungen!.map(z => (
                    <div key={z.id} className="flex items-center justify-between gap-2 text-slate-600 dark:text-slate-400">
                      <span>
                        {datum(z.datum)} · {z.quelle === 'bank' ? t('rechnungen.bankeingang')
                          : z.quelle === 'gutschrift' ? (z.notiz || t('rechnungen.perGutschrift'))
                          : t(ZAHLWEG_TEXT[z.zahlart ?? 'ueberweisung'] ?? 'rechnungen.zahlweg.ueberweisung')}
                        {z.notiz && z.quelle !== 'gutschrift' ? ` · ${z.notiz}` : ''}
                      </span>
                      <span className="flex items-center gap-2">
                        −{euro(z.betrag)}
                        {z.quelle !== 'bank' && z.quelle !== 'gutschrift' && offen.status !== 'storniert' && (
                          <button type="button" title={t('rechnungen.zahlungEntfernen')} aria-label={t('rechnungen.zahlungEntfernen')}
                                  onClick={() => zahlungEntfernen(offen, z.id)}
                                  className="rounded px-1 text-slate-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/40">×</button>
                        )}
                      </span>
                    </div>
                  ))}
                  {offen.status === 'gestellt' && (
                    <div className="flex justify-between font-semibold text-amber-700 dark:text-amber-400">
                      <span>{t('rechnungen.nochOffen')}</span><span>{euro(offenerBetrag(offen))}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
            {(offen.status === 'gestellt' || offen.status === 'bezahlt') && (
              <div className="flex shrink-0 justify-center sm:w-48">
                {offen.status === 'bezahlt' ? <div className="py-3"><BezahltStempel r={offen} /></div> : <GiroCode r={offen} />}
              </div>
            )}
            </div>

            <div className="flex flex-wrap gap-2">
              {offen.status !== 'entwurf' && (
                <Knopf onClick={() => drucken(offen)}>{t('rechnungen.drucken')}</Knopf>
              )}
              {(offen.status === 'gestellt' || offen.status === 'bezahlt') && (
                <Knopf onClick={() => lieferscheinDrucken(offen)}>
                  {offen.lieferschein ? t('rechnungen.lieferscheinDrucken') : t('rechnungen.lieferscheinErstellen')}
                </Knopf>
              )}
              {offen.status === 'entwurf' && (
                <Knopf variante="primaer" onClick={() => statusSetzen(offen, 'gestellt')}>{t('rechnungen.stellen')}</Knopf>
              )}
              {offen.status === 'gestellt' && !zahlungForm && (
                <Knopf variante="primaer" onClick={() => setZahlungForm({
                  betrag: String(offenerBetrag(offen)).replace('.', ','), datum: heute(), zahlart: offen.zahlweg ?? 'ueberweisung',
                })}>{t('rechnungen.zahlungErfassen')}</Knopf>
              )}
              {offen.gutschrift ? (
                <Knopf onClick={() => gutschriftDrucken(offen)}>
                  {t('rechnungen.gutschriftDrucken')} {offen.gutschrift.nummer ?? ''}
                </Knopf>
              ) : offen.status !== 'storniert' && offen.status !== 'entwurf' && !gutschriftForm && (
                <Knopf onClick={() => { setZahlungForm(null); setGutschriftForm({ mengen: offen.positionen.map(p => String(p.menge).replace('.', ',')), grund: '' }); }}>
                  {t('rechnungen.gutschriftErstellen')}
                </Knopf>
              )}
              {offen.status !== 'storniert' && offen.status !== 'entwurf' && (
                <Knopf variante="gefahr" onClick={() => statusSetzen(offen, 'storniert')}>{t('rechnungen.stornieren')}</Knopf>
              )}
            </div>

            {gutschriftForm && !offen.gutschrift && (
              <form onSubmit={e => { e.preventDefault(); void gutschriftStellen(offen); }}
                    className="space-y-3 rounded-lg border border-amber-200 bg-amber-50/50 p-4 dark:border-amber-900 dark:bg-amber-950/20">
                <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{t('rechnungen.gutschriftTitel')}</p>
                <div className="space-y-2">
                  {offen.positionen.map((p, i) => (
                    <div key={i} className="flex items-center gap-3 text-sm">
                      <span className="min-w-0 flex-1 truncate text-slate-700 dark:text-slate-300">{p.bezeichnung}</span>
                      <span className="text-xs text-slate-500">/ {menge(p.menge, p.einheit)}</span>
                      <input aria-label={`${t('rechnungen.gutschriftMenge')}: ${p.bezeichnung}`} inputMode="decimal"
                             className="w-24 rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-right text-sm dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                             value={gutschriftForm.mengen[i] ?? ''}
                             onChange={e => setGutschriftForm(f => f && ({ ...f, mengen: f.mengen.map((m, k) => k === i ? e.target.value : m) }))} />
                    </div>
                  ))}
                </div>
                <Feld label={t('rechnungen.gutschriftGrund')} value={gutschriftForm.grund}
                      onChange={e => setGutschriftForm(f => f && ({ ...f, grund: e.target.value }))} />
                <p className="text-xs text-slate-500 dark:text-slate-400">{t('rechnungen.gutschriftHinweis')}</p>
                <div className="flex flex-wrap gap-2">
                  <Knopf type="submit" variante="primaer" laedt={sendet}>{t('rechnungen.gutschriftStellen')}</Knopf>
                  <Knopf type="button" onClick={() => setGutschriftForm(null)}>{t('aktion.abbrechen')}</Knopf>
                </div>
              </form>
            )}

            {offen.status === 'gestellt' && zahlungForm && (
              <form onSubmit={e => { e.preventDefault(); void zahlungSpeichern(offen); }}
                    className="space-y-3 rounded-lg border border-marke-200 bg-marke-50/50 p-4 dark:border-marke-900 dark:bg-marke-950/20">
                <div className="grid gap-3 sm:grid-cols-3">
                  <Feld label={t('rechnungen.betrag')} value={zahlungForm.betrag} inputMode="decimal" required autoFocus
                        onChange={e => setZahlungForm(f => f && ({ ...f, betrag: e.target.value }))} />
                  <Feld label={t('rechnungen.zahltag')} type="date" value={zahlungForm.datum} max={heute()} required
                        onChange={e => setZahlungForm(f => f && ({ ...f, datum: e.target.value }))} />
                  <Auswahl label={t('rechnungen.zahlart')} value={zahlungForm.zahlart}
                           onChange={e => setZahlungForm(f => f && ({ ...f, zahlart: e.target.value }))}>
                    {(['ueberweisung', 'bar', 'karte'] as const).map(z => <option key={z} value={z}>{t(ZAHLWEG_TEXT[z])}</option>)}
                  </Auswahl>
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400">{t('rechnungen.ratenHinweis')}</p>
                <div className="flex flex-wrap gap-2">
                  <Knopf type="submit" variante="primaer" laedt={sendet}>
                    {Math.abs(zahlEingabe(zahlungForm.betrag) - offenerBetrag(offen)) < 0.005
                      ? t('rechnungen.alsBezahltSpeichern') : t('rechnungen.teilzahlungSpeichern')}
                  </Knopf>
                  <Knopf type="button" onClick={() => setZahlungForm(null)}>{t('aktion.abbrechen')}</Knopf>
                </div>
              </form>
            )}
          </div>
        </Fenster>
      )}

      <Karte>
        <div className="border-b border-slate-200 p-4 dark:border-slate-800">
          <Auswahl label={t('feld.status')} value={status} onChange={e => setStatus(e.target.value)}>
            <option value="">{t('status.alle')}</option>
            <option value="entwurf">{t('status.entwuerfe')}</option>
            <option value="gestellt">{t('status.offen')}</option>
            <option value="bezahlt">{t('status.bezahlt')}</option>
            <option value="storniert">{t('status.storniert')}</option>
          </Auswahl>
        </div>

        {laedt && !daten ? (
          <div className="flex justify-center py-12"><Spinner /></div>
        ) : liste.length === 0 ? (
          <LeerZustand titel={t('rechnungen.leer')} text={t('rechnungen.leerText')} />
        ) : (
          <ul className="divide-y divide-slate-200 dark:divide-slate-800">
            {liste.map(r => (
              <li key={r.id}>
                <button onClick={() => { setOffen(r); setZahlungForm(null); setGutschriftForm(null); setSendeFehler(null); }}
                        className="flex w-full flex-wrap items-center gap-3 px-5 py-3.5 text-left hover:bg-slate-50 dark:hover:bg-slate-800/50">
                  <div className="min-w-[180px] flex-1">
                    <p className="font-medium text-slate-900 dark:text-slate-100">
                      {r.nummer || 'Entwurf'} · {r.kundeName}
                    </p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {datum(r.datum)}
                      {r.status === 'gestellt' && r.faelligAm && ` · fällig ${datum(r.faelligAm)}`}
                      {r.status === 'gestellt' && (r.zahlungen?.length ?? 0) > 0 && (
                        <span className="text-amber-700 dark:text-amber-400">
                          {' · '}{t('rechnungen.teilbezahlt').replace('{betrag}', euro(offenerBetrag(r)))}
                        </span>
                      )}
                      {r.status === 'bezahlt' && r.bezahltPer === 'bank' && ` · ${t('rechnungen.perBank')}`}
                      {r.status === 'bezahlt' && r.bezahltPer === 'gutschrift' && ` · ${t('rechnungen.perGutschrift')}`}
                      {r.gutschrift && r.bezahltPer !== 'gutschrift' && ` · ${t('rechnungen.gutschriftVorhanden').replace('{nummer}', r.gutschrift.nummer ?? '')}`}
                    </p>
                  </div>
                  <Abzeichen art={STATUS_FARBE[r.status]}>{r.status}</Abzeichen>
                  <span className="w-28 text-right font-medium text-slate-900 dark:text-slate-100">{euro(r.brutto)}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Karte>
    </div>
  );
}
